// utils/responseTiming.ts
// Visit Response Timing: visits per hour since the ad, in bins that widen with time.
//
// The backend sends visits for each whole hour 0..167 since the impression the visit is
// attributed to (the household's most recent). Bins of unequal width must be read as a rate,
// visits per hour, or the widest bin wins by being wide: 72 % of visits fell in days 1–7, yet
// those 144 hours ran at a twentieth of the first hour's pace (LM, 2026-10-24).

export interface HourlyVisits {
    hour: number;
    visits: number;
}

export interface TimingBin {
    label: string;
    /** First hour in the bin, inclusive */
    from: number;
    /** Last hour in the bin, exclusive */
    to: number;
    visits: number;
    /** Share of all visits in the window, 0..100 */
    share: number;
    /** visits ÷ the bin's width in hours: the bar's height */
    perHour: number;
}

/** Narrow where the response is fast, wide where it is steady */
export const TIMING_BINS: ReadonlyArray<{ label: string; from: number; to: number }> = [
    { label: '0–1h', from: 0, to: 1 },
    { label: '1–3h', from: 1, to: 3 },
    { label: '3–6h', from: 3, to: 6 },
    { label: '6–12h', from: 6, to: 12 },
    { label: '12–24h', from: 12, to: 24 },
    { label: '1–2d', from: 24, to: 48 },
    { label: '2–4d', from: 48, to: 96 },
    { label: '4–7d', from: 96, to: 168 },
];

/** The three buckets older backends send, as bins, so the same rate chart serves both */
const LEGACY_BINS = [
    { label: 'Within an hour', from: 0, to: 1, key: 'Hour_1' },
    { label: '1 h – 1 day', from: 1, to: 24, key: 'Day_1' },
    { label: '1–7 days', from: 24, to: 168, key: 'Week_1' },
] as const;

const finish = (bins: Array<{ label: string; from: number; to: number; visits: number }>): TimingBin[] => {
    const total = bins.reduce((sum, b) => sum + b.visits, 0);
    return bins.map((b) => ({
        ...b,
        share: total > 0 ? (b.visits / total) * 100 : 0,
        perHour: b.visits / (b.to - b.from),
    }));
};

/**
 * Bins from the hourly series. An hour outside 0..167 (clock skew; the backend keeps rather
 * than hides it) belongs to no bin and is left out.
 */
export const binHourlyVisits = (rows: ReadonlyArray<HourlyVisits>): TimingBin[] =>
    finish(
        TIMING_BINS.map((bin) => ({
            ...bin,
            visits: rows
                .filter((r) => r.hour >= bin.from && r.hour < bin.to)
                .reduce((sum, r) => sum + (Number(r.visits) || 0), 0),
        }))
    );

/** Bins from the 1 hour / 1 day / 1 week sums, for a backend without the hourly series */
export const binLegacyBuckets = (summary: Record<string, unknown>): TimingBin[] =>
    finish(LEGACY_BINS.map(({ key, ...bin }) => ({ ...bin, visits: Number(summary[key]) || 0 })));

/** Whichever the response carries: the hourly series when present */
export const responseTimingBins = (response: unknown): TimingBin[] => {
    const r = (response ?? {}) as { table_data?: HourlyVisits[]; summary_data?: Record<string, unknown> };
    if (Array.isArray(r.table_data) && r.table_data.length) return binHourlyVisits(r.table_data);
    return binLegacyBuckets(r.summary_data ?? {});
};

/**
 * Below this many visits the curve's shape is mostly chance (a campaign peaking at 5 visits an
 * hour showed 1–3h dipping under 3–6h), so the bars are muted, as chart rule 9 does for ratios.
 */
export const MIN_TIMING_VISITS = 200;

export interface TimingSummary {
    total: number;
    /** Shares of all visits, 0..100 */
    withinHour: number;
    withinDay: number;
    /**
     * Hours by which half the visits had arrived: the end of the whole hour holding the middle
     * visit, so 1 means "within the first hour". Null without the hourly series.
     */
    medianHours: number | null;
}

export const responseTimingSummary = (response: unknown): TimingSummary => {
    const r = (response ?? {}) as { table_data?: HourlyVisits[]; summary_data?: Record<string, unknown> };
    if (Array.isArray(r.table_data) && r.table_data.length) {
        const rows = r.table_data
            .filter((x) => x.hour >= 0 && x.hour < 168)
            .map((x) => ({ hour: x.hour, visits: Number(x.visits) || 0 }))
            .sort((a, b) => a.hour - b.hour);
        const total = rows.reduce((s, x) => s + x.visits, 0);
        const sumBelow = (h: number) => rows.filter((x) => x.hour < h).reduce((s, x) => s + x.visits, 0);
        let medianHours: number | null = null;
        let running = 0;
        for (const x of rows) {
            running += x.visits;
            if (total > 0 && running * 2 >= total) {
                medianHours = x.hour + 1;
                break;
            }
        }
        return {
            total,
            withinHour: total > 0 ? (sumBelow(1) / total) * 100 : 0,
            withinDay: total > 0 ? (sumBelow(24) / total) * 100 : 0,
            medianHours,
        };
    }
    const s = r.summary_data ?? {};
    const [h, d, w] = [s.Hour_1, s.Day_1, s.Week_1].map((v) => Number(v) || 0);
    const total = h + d + w;
    return {
        total,
        withinHour: total > 0 ? (h / total) * 100 : 0,
        withinDay: total > 0 ? ((h + d) / total) * 100 : 0,
        medianHours: null,
    };
};

/** "1 h", "26 h", "3.2 days": hours up to two days, then days */
export const formatWaitHours = (hours: number): string =>
    hours < 48 ? `${hours} h` : `${(hours / 24).toFixed(1)} days`;
